import { useCallback, useEffect, useState } from "react";
import {
  Shield,
  ShieldCheck,
  Copy,
  CheckCircle2,
  KeyRound,
  Monitor,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  changePassword,
  confirmMfaEnrollment,
  disableMfa,
  listSessions,
  revokeAllOtherSessions,
  revokeSession,
  startMfaEnrollment,
  type SessionInfo,
} from "@/api/auth";

export function SecuritySettings() {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();

  // Password change
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  // MFA enrollment
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [otpUri, setOtpUri] = useState("");
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollStep, setEnrollStep] = useState<"intro" | "scan" | "backup">("intro");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [mfaLoading, setMfaLoading] = useState(false);

  // Disable MFA
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState("");

  // Sessions
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionBusy, setSessionBusy] = useState<string | null>(null);

  const reloadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const rows = await listSessions();
      setSessions(rows);
    } catch {
      // non-fatal — empty list will render
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadSessions();
  }, [reloadSessions]);

  const revokeOne = async (id: string) => {
    setSessionBusy(id);
    try {
      await revokeSession(id);
      addToast("success", "Session revoked");
      await reloadSessions();
    } catch {
      addToast("error", "Could not revoke session");
    } finally {
      setSessionBusy(null);
    }
  };

  const revokeOthers = async () => {
    setSessionBusy("__all");
    try {
      const n = await revokeAllOtherSessions();
      addToast("success", `Revoked ${n} session${n === 1 ? "" : "s"}`);
      await reloadSessions();
    } catch {
      addToast("error", "Could not revoke sessions");
    } finally {
      setSessionBusy(null);
    }
  };

  const changePw = async () => {
    if (newPw.length < 8) {
      addToast("error", "Password must be at least 8 characters");
      return;
    }
    if (newPw !== confirmPw) {
      addToast("error", "Passwords do not match");
      return;
    }
    setPwLoading(true);
    try {
      await changePassword(currentPw, newPw);
      addToast("success", "Password updated");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  const beginMfa = async () => {
    setMfaLoading(true);
    try {
      const res = await startMfaEnrollment();
      setSecret(res.secret);
      setOtpUri(res.otpauth_uri);
      setEnrollStep("scan");
      setEnrollOpen(true);
    } catch {
      addToast("error", "Failed to start MFA enrollment");
    } finally {
      setMfaLoading(false);
    }
  };

  const confirmMfa = async () => {
    setMfaLoading(true);
    try {
      const res = await confirmMfaEnrollment(enrollCode);
      setBackupCodes(res.backup_codes);
      setEnrollStep("backup");
      await refreshUser();
      addToast("success", "MFA enabled");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Invalid code");
    } finally {
      setMfaLoading(false);
    }
  };

  const turnOffMfa = async () => {
    setMfaLoading(true);
    try {
      await disableMfa(disablePw);
      await refreshUser();
      addToast("success", "MFA disabled");
      setDisableOpen(false);
      setDisablePw("");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Failed");
    } finally {
      setMfaLoading(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast("success", "Copied to clipboard");
  };

  return (
    <div className="space-y-6">
      {/* MFA */}
      <Card>
        <CardHeader>
          <CardTitle>Two-factor authentication</CardTitle>
        </CardHeader>
        <div className="flex items-start gap-4">
          <div
            className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              user?.mfa_enabled
                ? "bg-success/10 text-success"
                : "bg-warning/10 text-warning"
            }`}
          >
            {user?.mfa_enabled ? (
              <ShieldCheck className="h-5 w-5" />
            ) : (
              <Shield className="h-5 w-5" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-text-primary">
              {user?.mfa_enabled ? "MFA is active" : "MFA is not configured"}
            </p>
            <p className="text-xs text-text-muted mt-0.5">
              {user?.mfa_enabled
                ? "Your account requires a 6-digit authenticator code at sign-in."
                : "Add a second factor for stronger account security."}
            </p>
          </div>
          {user?.mfa_enabled ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDisableOpen(true)}
            >
              Disable
            </Button>
          ) : (
            <Button size="sm" onClick={beginMfa} isLoading={mfaLoading}>
              Enable MFA
            </Button>
          )}
        </div>
      </Card>

      {/* Active sessions */}
      <Card>
        <CardHeader>
          <CardTitle>Active sessions</CardTitle>
        </CardHeader>
        <div className="space-y-3">
          <p className="text-xs text-text-muted">
            Devices where you're signed in. Revoking a session immediately
            invalidates its refresh token.
          </p>

          {sessionsLoading ? (
            <div className="text-sm text-text-muted">Loading…</div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-text-muted">No active sessions.</div>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {sessions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-start gap-3 px-4 py-3"
                >
                  <div className="h-9 w-9 rounded-lg bg-primary-500/10 text-primary-600 flex items-center justify-center shrink-0">
                    <Monitor className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {s.user_agent || "Unknown device"}
                      </p>
                      {s.is_current && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-success">
                          This device
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-0.5">
                      {s.ip_address || "Unknown IP"} · signed in{" "}
                      {new Date(s.created_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-text-muted">
                      Expires {new Date(s.expires_at).toLocaleString()}
                    </p>
                  </div>
                  {!s.is_current && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => revokeOne(s.id)}
                      isLoading={sessionBusy === s.id}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {sessions.filter((s) => !s.is_current).length > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={revokeOthers}
                isLoading={sessionBusy === "__all"}
              >
                Sign out all other devices
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
        </CardHeader>
        <div className="space-y-4 max-w-md">
          <Input
            label="Current password"
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
          />
          <Input
            label="New password"
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <Input
            label="Confirm new password"
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
          <Button onClick={changePw} isLoading={pwLoading}>
            Update password
          </Button>
        </div>
      </Card>

      {/* Enrollment modal */}
      <Modal
        isOpen={enrollOpen}
        onClose={() => {
          setEnrollOpen(false);
          setEnrollStep("intro");
          setEnrollCode("");
        }}
        title="Enable two-factor authentication"
      >
        {enrollStep === "scan" && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-primary font-medium">
                1. Scan this in your authenticator app
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                Google Authenticator, 1Password, Authy, etc.
              </p>
            </div>
            <div className="rounded-lg bg-background border border-border p-4 font-mono text-xs break-all">
              {otpUri}
            </div>
            <div>
              <p className="text-sm text-text-primary font-medium">
                Or enter secret manually
              </p>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-xs font-mono rounded bg-background border border-border px-3 py-2">
                  {secret}
                </code>
                <Button variant="ghost" size="sm" onClick={() => copy(secret)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm text-text-primary font-medium mb-2">
                2. Enter the 6-digit code
              </p>
              <Input
                value={enrollCode}
                onChange={(e) =>
                  setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                icon={<KeyRound className="h-4 w-4" />}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEnrollOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={confirmMfa}
                isLoading={mfaLoading}
                disabled={enrollCode.length !== 6}
              >
                Verify & enable
              </Button>
            </div>
          </div>
        )}
        {enrollStep === "backup" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-5 w-5" />
              <p className="text-sm font-medium">MFA enabled</p>
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                Save your backup codes
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                Each code can be used once if you lose access to your authenticator.
                Store them somewhere safe — you won't see them again.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((c) => (
                <div
                  key={c}
                  className="rounded bg-background border border-border px-3 py-2 text-center"
                >
                  {c}
                </div>
              ))}
            </div>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => copy(backupCodes.join("\n"))}
            >
              <Copy className="h-4 w-4" />
              Copy all codes
            </Button>
            <Button className="w-full" onClick={() => setEnrollOpen(false)}>
              I've saved my codes
            </Button>
          </div>
        )}
      </Modal>

      {/* Disable modal */}
      <Modal
        isOpen={disableOpen}
        onClose={() => {
          setDisableOpen(false);
          setDisablePw("");
        }}
        title="Disable two-factor authentication"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Confirm your password to disable MFA. You can re-enable it anytime.
          </p>
          <Input
            label="Password"
            type="password"
            value={disablePw}
            onChange={(e) => setDisablePw(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDisableOpen(false)}>
              Cancel
            </Button>
            <Button onClick={turnOffMfa} isLoading={mfaLoading}>
              Disable MFA
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
